// src/pages/Institutional/HowItWorks.js
import React from 'react'
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Chip
} from '@mui/material'
import {
  Visibility,
  ChatBubbleOutline,
  FilterList,
  VerifiedUser,
  WhatsApp,
  Email,
  Message
} from '@mui/icons-material'

const HowItWorks = () => {
  const searchSteps = [
    {
      icon: <FilterList color='secondary' />,
      title: 'Buscar imóveis pelo filtro',
      description:
        'Filtre por tipo de imóvel, faixa de preço, número de quartos, tamanho, características e localização. O portal exibe resultados claros, organizados e com fotos de qualidade.'
    },
    {
      icon: <Visibility color='secondary' />,
      title: 'Ver detalhes completos',
      description:
        'Cada anúncio contém galeria de fotos, descrição, características, preço, localização aproximada, dados do anunciante e informações adicionais quando disponíveis.'
    },
    {
      icon: <ChatBubbleOutline color='secondary' />,
      title: 'Entrar em contato diretamente',
      description:
        'O contato é feito de forma rápida via WhatsApp ou formulário de mensagem. Todos os contatos vão direto para o anunciante.'
    }
  ]

  const advertiseSteps = [
    {
      number: '1',
      title: 'Criar uma conta',
      description:
        'Escolha seu tipo de conta: proprietário, corretor (com CRECI) ou imobiliária.'
    },
    {
      number: '2',
      title: 'Preencher os dados do imóvel',
      description:
        'Adicione fotos (mínimo recomendado: 6), descrição, tipo, preço, características, localização e vídeos (opcional).'
    },
    {
      number: '3',
      title: 'Publicação',
      description:
        'Após preencher, o anúncio entra no portal e aparece nos resultados de busca de acordo com os filtros.'
    },
    {
      number: '4',
      title: 'Recebimento de contatos',
      description:
        'Todos os leads chegam diretamente via WhatsApp, mensagem no portal ou e-mail (opcional).'
    }
  ]

  const rankingFactors = [
    {
      label: 'Relevância',
      description:
        'O anúncio aparece somente onde faz sentido (cidade, tipo, características).'
    },
    {
      label: 'Qualidade',
      description:
        'Anúncios com boas fotos e informações completas ganham prioridade.'
    },
    {
      label: 'Engajamento',
      description:
        'Imóveis que despertam mais interesse sobem naturalmente no ranking.'
    }
  ]

  const curatoriaFeatures = [
    'Imóveis bonitos',
    'Bem apresentados',
    'Com preço competitivo',
    'Procurados pelo público'
  ]

  const professionalFeatures = [
    'Anúncios ilimitados',
    'Ferramenta de publicação rápida',
    'Validação de CRECI',
    'Painel para gerenciamento',
    'Leads diretos',
    'Ranking justo e inteligente',
    'Possibilidade de integração com CRM (futuro)'
  ]

  const securityFeatures = [
    'Validação básica de informações',
    'Políticas de moderação',
    'Verificação de CRECI para contas profissionais',
    'Filtros organizados que reduzem duplicidade',
    'Ambiente seguro para anunciar e buscar imóveis'
  ]

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 6 }}>
      <Container maxWidth='lg'>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant='h3'
            sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}
          >
            Como Funciona
          </Typography>
          <Typography
            variant='h6'
            sx={{
              color: 'text.secondary',
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            A umarkt foi criado para facilitar a forma como pessoas anunciam,
            encontram e se conectam com oportunidades imobiliárias em todo o
            Brasil.
          </Typography>
          <Typography
            variant='body1'
            sx={{
              color: 'text.secondary',
              maxWidth: '900px',
              mx: 'auto',
              mt: 2,
              lineHeight: 1.6
            }}
          >
            Somos um marketplace moderno, projetado para ser rápido, organizado
            e fácil de usar, tanto para quem procura quanto para quem anuncia.
          </Typography>
        </Box>

        {/* Para quem está buscando um imóvel */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h4'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            1. Para quem está buscando um imóvel
          </Typography>
          <Typography variant='body1' sx={{ mb: 3, color: 'text.secondary' }}>
            Navegar na umarkt é simples. O usuário pode:
          </Typography>
          <Grid container spacing={3}>
            {/*  xs={12} md={4} */}
            {searchSteps.map((step, index) => (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}
                key={index}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-4px)',
                      boxShadow: '0px 8px 24px rgba(255, 127, 39, 0.15)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      color: 'primary.main'
                    }}
                  >
                    {React.cloneElement(step.icon, {
                      sx: { fontSize: '1.8rem' }
                    })}
                  </Box>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                  >
                    {step.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Para quem deseja anunciar um imóvel */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h4'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            2. Para quem deseja anunciar um imóvel
          </Typography>
          <Typography variant='body1' sx={{ mb: 3, color: 'text.secondary' }}>
            Anunciar é rápido e gratuito.
          </Typography>
          <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', borderRadius: 3 }}>
            <Grid container spacing={3}>
              {advertiseSteps.map((step, index) => (
                <Grid size={{ xs: 12 }} key={index}>
                  <Box
                    sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}
                  >
                    <Chip
                      label={step.number}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '1rem',
                        height: 40,
                        width: 40,
                        borderRadius: '50%'
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                      >
                        {step.description}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Ranking e Curadoria */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h4'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            3. Ranking e Curadoria do Portal
          </Typography>
          <Paper
            elevation={0}
            sx={{ p: 4, bgcolor: 'white', borderRadius: 3, mb: 3 }}
          >
            <Typography
              variant='body1'
              sx={{ mb: 3, lineHeight: 1.8, color: 'text.secondary' }}
            >
              Para oferecer a melhor experiência e exibir primeiro os anúncios
              mais relevantes, a umarkt utiliza um sistema inteligente baseado
              em:
            </Typography>
            <Grid container spacing={3}>
              {rankingFactors.map((factor, index) => (
                <Grid size={{ xs: 12, md: 4 }} key={index}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                      height: '100%'
                    }}
                  >
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}
                    >
                      {factor.label}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      {factor.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 4,
              bgcolor: 'primary.main',
              color: 'white',
              borderRadius: 3
            }}
          >
            <Typography variant='h5' sx={{ fontWeight: 700, mb: 2 }}>
              Curadoria Inteligente
            </Typography>
            <Typography variant='body1' sx={{ mb: 2, lineHeight: 1.8 }}>
              O portal identifica os anúncios mais atraentes e prioriza:
            </Typography>
            <Grid container spacing={1}>
              {curatoriaFeatures.map((feature, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'white'
                      }}
                    />
                    <Typography variant='body2'>{feature}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Typography variant='body1' sx={{ mt: 3, fontWeight: 600 }}>
              Destaques (opcional)
            </Typography>
            <Typography variant='body2' sx={{ lineHeight: 1.7 }}>
              Corretores e imobiliárias podem impulsionar anúncios para ganharem
              maior visibilidade.
            </Typography>
          </Paper>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Para Corretores e Imobiliárias */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h4'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            4. Para Corretores e Imobiliárias
          </Typography>
          <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', borderRadius: 3 }}>
            <Typography variant='body1' sx={{ mb: 3, color: 'text.secondary' }}>
              Se você é corretor ou imobiliária, o portal oferece:
            </Typography>
            <Grid container spacing={2}>
              {professionalFeatures.map((feature, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <VerifiedUser
                      sx={{ color: 'success.main', fontSize: '1.2rem' }}
                    />
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary' }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Segurança e Confiabilidade */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h4'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            5. Segurança e Confiabilidade
          </Typography>
          <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', borderRadius: 3 }}>
            <Typography variant='body1' sx={{ mb: 3, color: 'text.secondary' }}>
              Nossa plataforma possui:
            </Typography>
            <Grid container spacing={2}>
              {securityFeatures.map((feature, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'primary.main'
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary' }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Transparência */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            bgcolor: 'grey.50',
            borderRadius: 3,
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            mb: 6
          }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}
          >
            6. Transparência
          </Typography>
          <Typography
            variant='body1'
            sx={{ mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
          >
            A umarkt não interfere na negociação entre usuários, não cobra
            comissões e não participa de intermediação.
          </Typography>
          <Typography
            variant='body1'
            sx={{ mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
          >
            Nosso modelo é baseado em:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {[
              'Visibilidade',
              'Organização',
              'Qualidade dos anúncios',
              'Tecnologia',
              'Experiência de usuário'
            ].map((item, index) => (
              <Chip
                key={index}
                label={item}
                sx={{
                  bgcolor: 'white',
                  fontWeight: 500,
                  borderColor: 'grey.300'
                }}
                variant='outlined'
              />
            ))}
          </Box>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary' }}
          >
            Toda a comunicação ocorre diretamente entre comprador e anunciante.
          </Typography>
        </Paper>

        {/* Suporte e Atendimento */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}
          >
            7. Suporte e Atendimento
          </Typography>
          <Typography variant='body1' sx={{ color: 'text.secondary', mb: 3 }}>
            Em caso de dúvidas, sugestões ou problemas:
          </Typography>
          <Grid container spacing={2} sx={{ maxWidth: '600px', mx: 'auto' }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Email
                  sx={{ color: 'primary.main', fontSize: '2rem', mb: 1 }}
                />
                <Typography
                  variant='subtitle2'
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  E-mail
                </Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  contato@umarkt.com.br
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <WhatsApp
                  sx={{ color: 'success.main', fontSize: '2rem', mb: 1 }}
                />
                <Typography
                  variant='subtitle2'
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  WhatsApp
                </Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  (11) 9 8139-6930
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Message sx={{ color: 'info.main', fontSize: '2rem', mb: 1 }} />
                <Typography
                  variant='subtitle2'
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Central de Ajuda
                </Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  Em desenvolvimento
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          <Typography variant='body2' sx={{ color: 'text.secondary', mt: 3 }}>
            Estamos sempre aprimorando o marketplace para oferecer a melhor
            experiência possível.
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

export default HowItWorks
